import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { flattenToAppURL } from '@plone/volto/helpers';
import { getContent } from '@plone/volto/actions';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import arrowRightSVG from 'fcmh/theme/assets/icons/arrow-right.svg';
import { useIntl } from 'react-intl';
import messages from '../../../messages';

const safe = (s) => (typeof s === 'string' ? s : '');

const resolveLink = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (Array.isArray(val)) return resolveLink(val[0]);
  if (val['@id']) return flattenToAppURL(val['@id']);
  if (val.url) return val.url;
  return '';
};

const getImageURL = (item) => {
  if (!item) return null;

  try {
    const previewImageLink = item?.preview_image_link;
    if (previewImageLink?.image_scales?.image?.[0]?.base_path) {
      const origin =
        typeof window !== 'undefined' ? window.location.origin : '';
      return `${origin}${previewImageLink.image_scales.image[0].base_path}/@@images/image`;
    }

    const scales = item?.image_scales?.preview_image_link;
    if (Array.isArray(scales) && scales[0]?.base_path) {
      const origin =
        typeof window !== 'undefined' ? window.location.origin : '';
      return `${origin}${scales[0].base_path}/@@images/image`;
    }

    const base = flattenToAppURL(item?.['@id'] || item?.getURL || '');
    if (base) {
      const origin =
        typeof window !== 'undefined' ? window.location.origin : '';
      return `${origin}${base}/@@images/preview_image/preview`;
    }
  } catch {
    return null;
  }

  return null;
};

const extractPreviewCopyright = (item) => {
  if (!item) return '';

  const direct =
    item.preview_caption ||
    item.previewCaption ||
    item.preview_image_caption ||
    item.previewImageCaption ||
    item.preview_image_legend ||
    item.previewImageLegend ||
    item.image_caption ||
    item.imageCaption ||
    item.legend ||
    item.rights ||
    item.copyright ||
    '';

  const nested =
    item.preview_image?.caption ||
    item.preview_image?.legend ||
    item.preview_image?.rights ||
    item.preview_image?.copyright ||
    '';

  const val = direct || nested;

  return safe(val).trim();
};

const ResearchCard = ({ item, index, isEditMode }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const rawId = item?.['@id'] || item?.getURL || '';
  const itemUrl = rawId ? flattenToAppURL(rawId) : '#';
  const imageSrc = getImageURL(item);

  const appURL = rawId ? flattenToAppURL(rawId) : null;

  const sub = useSelector((state) =>
    appURL ? state.content.subrequests?.[appURL] : null,
  );
  const sourceContent = sub?.data;

  React.useEffect(() => {
    if (appURL && !sourceContent && !sub?.loading) {
      dispatch(getContent(appURL, null, appURL));
    }
  }, [appURL, sourceContent, sub?.loading, dispatch]);

  const copyrightText =
    extractPreviewCopyright(item) || extractPreviewCopyright(sourceContent);

  const [copyrightOpen, setCopyrightOpen] = React.useState(false);

  const renderCopyright = () => {
    if (!copyrightText) return null;

    const toggle = (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      setCopyrightOpen((o) => !o);
    };

    return (
      <div
        className={`research-card__copyright${copyrightOpen ? ' is-open' : ''}`}
        onMouseLeave={() => setCopyrightOpen(false)}
      >
        <button
          type="button"
          className="research-card__copyright-icon"
          aria-label="Bildnachweis anzeigen"
          aria-expanded={copyrightOpen}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setCopyrightOpen(false);
            }
          }}
        >
          ©
        </button>

        <div className="research-card__copyright-tooltip" role="tooltip">
          <span className="research-card__copyright-text">{copyrightText}</span>
        </div>
      </div>
    );
  };

  return (
    <article className="research-card" key={rawId || index}>
      {imageSrc && (
        <div className="research-card__image-box">
          <img
            src={imageSrc}
            alt={item?.title || ''}
            className="research-card__image"
            loading="lazy"
          />
          <span aria-hidden className="research-card__image-gradient" />
          <h3 className="research-card__title-overlay">
            {item?.title || 'Projekt'}
          </h3>
          {renderCopyright()}
        </div>
      )}

      <div className="research-card__body">
        {item?.description && (
          <p className="research-card__description">{item.description}</p>
        )}

        <UniversalLink
          href={itemUrl}
          className="research-card__link"
          aria-label={`Weiterlesen: ${item?.title || 'Projekt'}`}
          disabled={isEditMode}
        >
          <Icon name={arrowRightSVG} size="16px" />
          <span>{intl.formatMessage(messages.readMore)}</span>
        </UniversalLink>
      </div>
    </article>
  );
};

ResearchCard.propTypes = {
  item: PropTypes.any,
  index: PropTypes.number,
  isEditMode: PropTypes.bool,
};

const ResearchVariation = ({
  items = [],
  isEditMode = false,
  headline,
  moreLinkHref,
  moreLinkLabel,
}) => {
  const footerHref = resolveLink(moreLinkHref);

  return (
    <section className="research-section" aria-labelledby="research-heading">
      {headline && (
        <h2 id="research-heading" className="research-section__heading">
          {headline}
        </h2>
      )}

      <div className="research-grid">
        {items.map((item, index) => (
          <ResearchCard
            item={item}
            index={index}
            isEditMode={isEditMode}
            key={item?.['@id'] || item?.getURL || index}
          />
        ))}
      </div>

      {footerHref && moreLinkLabel && (
        <div className="research-section__footer">
          <UniversalLink href={footerHref} className="research-section__more">
            {moreLinkLabel}
          </UniversalLink>
        </div>
      )}
    </section>
  );
};

ResearchVariation.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any),
  isEditMode: PropTypes.bool,
  headline: PropTypes.string,
  moreLinkHref: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  moreLinkLabel: PropTypes.string,
};

export default ResearchVariation;
